import React, {FC, Dispatch, SetStateAction, useState, useEffect} from 'react';
import {CustomModal} from '../../../../../components/UI/AntCustom/CustomModal';
import {CustomFieldInput} from '../../../../../components/UI/AntCustom/CustomFieldInput';
import {CustomButton} from '../../../../../components/UI/AntCustom/CustomButton';
import {DebounceSelect} from '../../../../../components/UI/AntCustom/DebounceSelect';
import {CustomDatePicker} from '../../../../../components/UI/AntCustom/CustomDatePicker';
import {CustomSwitch} from '../../../../../components/UI/AntCustom/CustomSwitch';
import {CommonFormMessages} from '../../../../../intl/commonMessages/CommonFormMessages';
import {CommonDetailsMessages} from '../../../../../intl/commonMessages/CommonDetailsMessages';
import {CommonButtonMessages} from '../../../../../intl/commonMessages/CommonButtonMessages';
import {useIntl} from 'react-intl';
import styled, {createGlobalStyle} from 'styled-components';
import {Form, Row, Col} from 'antd';
import TextArea from 'antd/es/input/TextArea';
import {Slider} from 'antd';
import {CloseOutlined} from '@ant-design/icons';
import emptyCheckboxIconUrl from '../../../../../media/icons/empty-checkbox.svg';
import halfFilledCheckboxIconUrl from '../../../../../media/icons/half-filled-checkbox.svg';
import filledCheckboxIconUrl from '../../../../../media/icons/filled-checkbox.svg';
import {fetchPlaneModels} from '../../../../../utils/helpers/selectDataFetchers/fetchPlaneModels';
import {fetchOperators} from '../../../../../utils/helpers/selectDataFetchers/fetchOperators';
import {screenSizes} from '../../../../../utils/constans/styles/screenSizes';

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 32px 32px 12px 32px;
  border-bottom: 1px solid #dddddc;
`;

const ModalTitle = styled.div`
  font-size: 18px;
  font-weight: 400;
  line-height: 22px;
  color: #000;
`;

const CloseButton = styled.div`
  width: 16px;
  height: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #8c8c8b;
  
  &:hover {
    color: #000;
  }
`;

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 32px;
  max-height: calc(100vh - 280px);
  overflow-y: auto;
`;

const ModalFooter = styled.div`
  padding: 24px 32px 32px 32px;
`;

const SliderWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
  min-width: 0;
`;

const SliderLabels = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 10px;
  line-height: 12px;
  color: grey;
  height: 12px;
  margin-top: 8px;
`;

const StyledSlider = styled(Slider)`
  margin: 0;
  height: 32px;
  width: 100% !important;
  flex: 1;
  min-width: 0;
  
  .ant-slider-rail {
    background-color: #d9dad9;
    height: 6px;
    border-radius: 9999px;
    width: 100% !important;
    left: 0 !important;
    right: 0 !important;
  }
  
  .ant-slider-track {
    background-color: #1890ff;
    height: 6px;
    border-radius: 9999px;
  }
  
  .ant-slider-handle {
    width: 24px;
    height: 24px;
    border: none;
    background-color: #1890ff;
    box-shadow: none;
    margin-top: -9px;
    border-radius: 9999px;
    
    &:focus {
      box-shadow: none;
    }
    
    &:hover {
      border-color: #1890ff;
    }
  }
  
  .ant-slider-handle-1 {
    margin-left: 1% !important;
  }
  
  .ant-slider-handle-2 {
    margin-right: 1% !important;
  }
  
  .ant-slider-handle::before {
    display: none;
  }
  
  .ant-slider-handle::after {
    display: none;
  }
`;

const StyledModal = styled(CustomModal)`
  .ant-modal-content {
    border-radius: 20px;
    padding: 0;
    overflow: hidden;
    max-height: calc(100vh - 100px);
    display: flex;
    flex-direction: column;
  }
  
  .ant-modal-body {
    padding: 0;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
  }
  
  /* Убираем ограничения высоты для TextArea в десктопной версии, чтобы он мог расти без внутреннего скролла */
  @media (min-width: ${screenSizes.mobile + 1}px) {
    .ant-input-affix-wrapper-textarea-with-clear-btn {
      max-height: none !important;
      
      textarea.ant-input {
        max-height: none !important;
        overflow-y: clip !important;
        resize: none !important;
      }
    }
  }
  
  /* Добавляем padding-right в селектах для резервирования места под иконку очистки */
  .ant-select-selector {
    padding-right: 32px !important;
  }
`;

// Глобальные стили для выпадающего меню селекторов в модалке
const AdvancedSearchModalDropdownStyles = createGlobalStyle<{ parentOptionIndexes?: number[]; enableIndeterminate?: boolean }>`
  /* Стили для выпадающего меню селекторов в модалке расширенного поиска */
  .ant-select-dropdown {
    border-radius: 8px !important;
    padding: 12px !important;
    box-shadow: 0px 1px 4px rgba(12, 12, 13, 0.1), 0px 1px 4px rgba(12, 12, 13, 0.05) !important;
    
    .ant-select-item {
      min-height: 32px !important;
      height: auto !important;
      line-height: 20px !important;
      padding: 6px 0 !important;
      margin: 0 !important;
      border-radius: 0 !important;
      display: flex !important;
      align-items: flex-start !important;
      gap: 16px !important;
      
      &:not(:last-child) {
        margin-bottom: 8px !important;
      }
      
      /* Стили для вложенных опций (дочерние с отступом) */
      &[data-level="1"] {
        padding-left: 8px !important;
      }
      
      /* Стили для родительских опций (жирный шрифт) */
      &[data-is-parent="true"] {
        .ant-select-item-option-content {
          font-weight: 600 !important;
        }
      }
      
      .ant-select-item-option-content {
        display: flex !important;
        align-items: flex-start !important;
        justify-content: space-between !important;
        width: 100% !important;
        gap: 16px !important;
        font-size: 14px !important;
        font-weight: 500 !important;
        line-height: 20px !important;
        color: #181c25 !important;
        padding-right: 0 !important;
        white-space: normal !important;
        word-wrap: break-word !important;
        overflow-wrap: break-word !important;
        flex: 1 1 auto !important;
        min-width: 0 !important;
        
        /* Стили для текста label - разрешаем многострочность */
        > span:first-child,
        > div:first-child,
        > *:not(.option-count) {
          white-space: normal !important;
          word-wrap: break-word !important;
          overflow-wrap: break-word !important;
          flex: 1 1 auto !important;
          min-width: 0 !important;
        }
        
        .option-count {
          font-size: 14px !important;
          font-weight: 500 !important;
          line-height: 20px !important;
          color: #8c8c8b !important;
          margin-left: auto !important;
          margin-right: 0 !important;
          flex-shrink: 0 !important;
          white-space: nowrap !important;
        }
      }
      
      &:hover, &.ant-select-item-option-active, &.ant-select-item-option-selected {
        background-color: transparent !important;
      }
      
      /* Стили для числа в конце опции */
      .ant-select-item-option-content {
        display: flex !important;
        align-items: flex-start !important;
        justify-content: space-between !important;
        width: 100% !important;
        gap: 16px !important;
      }
    }
    
    &.ant-select-dropdown-multiple {
      .ant-select-item {
        /* Стили для чекбоксов в мультиселекте */
        .ant-select-item-option-state {
          display: flex !important;
          align-items: flex-start !important;
          justify-content: center !important;
          width: 20px !important;
          height: 16px !important;
          min-width: 20px !important;
          flex-shrink: 0 !important;
          order: -1 !important;
          position: relative !important;
          margin-top: 2px !important; /* Небольшой отступ сверху для выравнивания с текстом */
          
          /* Скрываем стандартную иконку галочки */
          .anticon.anticon-check {
            display: none !important;
          }
          
          /* Добавляем SVG иконку через псевдоэлемент */
          &::before {
            content: '' !important;
            display: block !important;
            width: 20px !important;
            height: 16px !important;
            background-image: url(${filledCheckboxIconUrl}) !important;
            background-size: 16px 16px !important;
            background-repeat: no-repeat !important;
            background-position: center center !important;
            position: absolute !important;
            top: 0 !important;
            left: 0 !important;
            z-index: 10 !important;
          }
        }

        /* Для выбранных опций показываем filled-checkbox */
        &:has(.ant-select-item-option-selected):not(.ant-select-item-option-active)::before {
          content: '' !important;
          display: block !important;
          width: 20px !important;
          height: 16px !important;
          background-image: url(${filledCheckboxIconUrl}) !important;
          background-size: 16px 16px !important;
          background-repeat: no-repeat !important;
          background-position: center center !important;
          position: absolute !important;
          top: 0 !important;
          left: 0 !important;
          z-index: 10 !important;
        }

        /* Для неактивных и невыбранных опций показываем empty-checkbox */
        &:not(.ant-select-item-option-selected)::before {
          content: '' !important;
          display: block !important;
          width: 20px !important;
          height: 16px !important;
          background-image: url(${emptyCheckboxIconUrl}) !important;
          background-size: 16px 16px !important;
          background-repeat: no-repeat !important;
          background-position: center center !important;
          z-index: 10 !important;
        }
      }
    }
    
    /* Скрываем кастомные иконки для обычных селектов (не мультиселектов) */
    &:not(.ant-select-dropdown-multiple) {
      .ant-select-item {
        &::before {
          display: none !important;
        }
        
        .ant-select-item-option-state::before {
          display: none !important;
        }
      }
    }
    
    /* Динамически добавляем half-filled-checkbox для частично выбранных групповых опций через nth-child (только для мультиселектов) */
    &.ant-select-dropdown-multiple {
      ${(props) => {
        if (!props.enableIndeterminate || !props.parentOptionIndexes || props.parentOptionIndexes.length === 0) {
          return '';
        }
        return props.parentOptionIndexes.map((index) => `
          .ant-select-item.ant-select-item-option:nth-child(${index}):not(.ant-select-item-option-selected)::before {
            background-image: url(${halfFilledCheckboxIconUrl}) !important;
          }
        `).join('');
      }}
    }
    
    .rc-virtual-list {
      padding: 0 !important;
    }
    
    .rc-virtual-list-holder {
      padding: 0 !important;
    }
  }
  
  /* Скрываем счетчик в выбранных чипах селекта */
  .ant-select-selection-item {
    .option-count {
      display: none !important;
    }
  }
`;

interface IAdvancedSearchModal {
  isModalVisible: boolean;
  setIsModalVisible: Dispatch<SetStateAction<boolean>>;
  onSave?: (values: any) => void;
  initialValues?: any;
}

export const AdvancedSearchModal: FC<IAdvancedSearchModal> = ({
  isModalVisible,
  setIsModalVisible,
  onSave,
  initialValues
}) => {
  const intl = useIntl();
  const [form] = Form.useForm();
  const currentYear = new Date().getFullYear();
  const minYear = currentYear - 100;
  const [productionYearRange, setProductionYearRange] = useState<[number, number]>([minYear, currentYear]);
  const [overhaulYearRange, setOverhaulYearRange] = useState<[number, number]>([minYear, currentYear]);
  const [flightDurationRange, setFlightDurationRange] = useState<[number, number]>([1, 12]);
  const [selectedSafetyStandards, setSelectedSafetyStandards] = useState<string[]>([]);
  

  // Функция для создания опций с кастомным label (включая число) - используется только для safetyStandards
  const createOptionWithCount = (label: string, value: string, count?: number, level?: number, isParent?: boolean) => {
    return {
      label: (
        <div 
          style={{
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between', 
            width: '100%',
            paddingLeft: level === 1 ? '8px' : '0',
          }}
          data-level={level}
          data-is-parent={isParent}
        >
          <span style={{ fontWeight: isParent ? 600 : 400 }}>{label}</span>
          {count !== undefined && (
            <span className="option-count" style={{marginLeft: 'auto', marginRight: '0', color: '#8c8c8b', fontSize: '14px', fontWeight: 500}}>{count}</span>
          )}
        </div>
      ) as any,
      value,
    };
  };

  // Константы для опций (без count, так как данные будут с бекенда)
  const landingsOptions = [
    { label: intl.formatMessage({id: 'app.form.options.landings.noLandings', defaultMessage: 'No landings'}), value: 'no-landings' },
    { label: intl.formatMessage({id: 'app.form.options.landings.oneLanding', defaultMessage: 'One landing'}), value: 'one-landing' },
    { label: intl.formatMessage({id: 'app.form.options.landings.twoPlusLandings', defaultMessage: 'Two or more landings'}), value: 'two-plus-landings' },
  ];

  const refuelsOptions = [
    { label: intl.formatMessage({id: 'app.form.options.refuels.noRefuels', defaultMessage: 'No refuelings'}), value: 'no-refuels' },
    { label: intl.formatMessage({id: 'app.form.options.refuels.oneRefuel', defaultMessage: 'One refueling'}), value: 'one-refuel' },
    { label: intl.formatMessage({id: 'app.form.options.refuels.twoRefuels', defaultMessage: 'Two refuelings'}), value: 'two-refuels' },
    { label: intl.formatMessage({id: 'app.form.options.refuels.threePlusRefuels', defaultMessage: 'Three or more refuelings'}), value: 'three-plus-refuels' },
  ];

  // Amenities - константы согласно API
  const amenitiesOptions = [
    { label: intl.formatMessage({id: 'app.form.options.amenities.airAmbulance', defaultMessage: 'Air ambulance'}), value: 'air_ambulance' },
    { label: intl.formatMessage({id: 'app.form.options.amenities.animalsAboard', defaultMessage: 'Animals aboard'}), value: 'animals_aboard' },
    { label: intl.formatMessage({id: 'app.form.options.amenities.smokingAboard', defaultMessage: 'Smoking aboard'}), value: 'smoking_aboard' },
    { label: intl.formatMessage({id: 'app.form.options.amenities.airCargoFreight', defaultMessage: 'Air cargo freight'}), value: 'air_cargo_freight' },
    { label: intl.formatMessage({id: 'app.form.options.amenities.wifi', defaultMessage: 'Wi-Fi'}), value: 'wifi' },
    { label: intl.formatMessage({id: 'app.form.options.amenities.ovenAboard', defaultMessage: 'Oven aboard'}), value: 'oven_aboard' },
  ];

  // Структура для вложенных опций стандартов безопасности
  interface NestedOption {
    label: string;
    value: string;
    count?: number;
    children?: NestedOption[];
    isParent?: boolean;
  }

  // Стандарты безопасности - пока статические данные, так как нет API endpoint
  // TODO: Заменить на вызов API когда появится endpoint
  const safetyStandardsNestedOptions: NestedOption[] = [
    {
      label: intl.formatMessage({id: 'app.form.options.safetyStandards.all', defaultMessage: 'All'}),
      value: 'all',
      isParent: true,
    },
    {
      label: intl.formatMessage({id: 'app.form.options.safetyStandards.argus', defaultMessage: 'Argus'}),
      value: 'argus',
      isParent: true,
      children: [
        { label: intl.formatMessage({id: 'app.form.options.safetyStandards.argusGold', defaultMessage: 'Gold'}), value: 'argus-gold' },
        { label: intl.formatMessage({id: 'app.form.options.safetyStandards.argusGoldPlus', defaultMessage: 'Gold+'}), value: 'argus-gold-plus' },
        { label: intl.formatMessage({id: 'app.form.options.safetyStandards.argusPlatinum', defaultMessage: 'Platinum'}), value: 'argus-platinum' },
      ],
    },
    {
      label: intl.formatMessage({id: 'app.form.options.safetyStandards.isBao', defaultMessage: 'IS-BAO'}),
      value: 'is-bao',
      isParent: true,
      children: [
        { label: intl.formatMessage({id: 'app.form.options.safetyStandards.isBaoRegistered', defaultMessage: 'Registered'}), value: 'is-bao-registered' },
      ],
    },
    {
      label: intl.formatMessage({id: 'app.form.options.safetyStandards.wyvern', defaultMessage: 'Wyvern'}),
      value: 'wyvern',
      isParent: true,
      children: [
        { label: intl.formatMessage({id: 'app.form.options.safetyStandards.wyvernRegistered', defaultMessage: 'Registered'}), value: 'wyvern-registered' },
        { label: intl.formatMessage({id: 'app.form.options.safetyStandards.wyvernWingman', defaultMessage: 'Wingman'}), value: 'wyvern-wingman' },
      ],
    },
  ];

  // Преобразуем вложенные опции в плоский список для Ant Design Select
  const flattenNestedOptions = (nested: NestedOption[]): any[] => {
    const result: any[] = [];
    nested.forEach((option) => {
      // Добавляем родительскую опцию
      result.push({
        label: option.label,
        value: option.value,
        count: option.count,
        isParent: option.isParent,
        level: 0,
      });
      // Добавляем дочерние опции
      if (option.children) {
        option.children.forEach((child) => {
          result.push({
            label: child.label,
            value: child.value,
            count: child.count,
            isParent: false,
            level: 1,
            parentValue: option.value,
          });
        });
      }
    });
    return result;
  };

  // Функция для определения состояния групповой опции (частично выбрана)
  const getParentOptionState = (parentValue: string, selectedValues: string[]): 'none' | 'partial' | 'all' => {
    if (parentValue === 'all') {
      // Для "Все" проверяем, выбраны ли все опции
      const allOptionsSelected = safetyStandardsNestedOptions
        .filter((opt) => opt.value !== 'all')
        .every((option) => {
          if (option.children) {
            return option.children.every((child) => selectedValues.includes(child.value));
          }
          return selectedValues.includes(option.value);
        });
      return allOptionsSelected ? 'all' : (selectedValues.some((v) => v !== 'all') ? 'partial' : 'none');
    }
    
    const parentOption = safetyStandardsNestedOptions.find((opt) => opt.value === parentValue);
    if (!parentOption || !parentOption.children) {
      return 'none';
    }
    
    const selectedChildren = parentOption.children.filter((child) => 
      selectedValues.includes(child.value)
    );
    
    if (selectedChildren.length === 0) {
      return 'none';
    } else if (selectedChildren.length === parentOption.children.length) {
      return 'all';
    } else {
      return 'partial';
    }
  };

  // Вычисляем индексы родительских опций для CSS nth-child
  const getParentOptionIndexes = (selectedValues: string[]) => {
    const indexes: number[] = [];
    const flattened = flattenNestedOptions(safetyStandardsNestedOptions);
    
    flattened.forEach((opt, index) => {
      if (opt.isParent && getParentOptionState(opt.value, selectedValues) === 'partial') {
        indexes.push(index + 1); // nth-child использует 1-based индексы
      }
    });
    
    return indexes;
  };

  const [parentOptionIndexes, setParentOptionIndexes] = useState<number[]>([]);

  // Обновляем индексы при изменении выбранных значений
  useEffect(() => {
    const indexes = getParentOptionIndexes(selectedSafetyStandards);
    setParentOptionIndexes(indexes);
  }, [selectedSafetyStandards]);

  const safetyStandardsOptions = flattenNestedOptions(safetyStandardsNestedOptions).map((opt) => 
    createOptionWithCount(opt.label, opt.value, undefined, opt.level, opt.isParent)
  );

  // Обработчик изменения выбора стандартов безопасности с поддержкой вложенности
  const handleSafetyStandardsChange = (selectedValues: any) => {
    // Преобразуем значения в строки, если они объекты
    const stringValues = Array.isArray(selectedValues) 
      ? selectedValues.map((v: any) => typeof v === 'string' ? v : v.value || v)
      : [];
    
    const previousValues = selectedSafetyStandards;
    const newSelectedValues = [...stringValues];

    // Определяем, какие опции были добавлены, а какие удалены
    const addedValues = stringValues.filter((v: string) => !previousValues.includes(v));
    const removedValues = previousValues.filter((v: string) => !stringValues.includes(v));

    // Обрабатываем добавленные опции
    addedValues.forEach((addedValue: string) => {
      // Если выбрано "Все", выбираем все опции
      if (addedValue === 'all') {
        safetyStandardsNestedOptions.forEach((option) => {
          if (option.value !== 'all' && !newSelectedValues.includes(option.value)) {
            newSelectedValues.push(option.value);
          }
          if (option.children) {
            option.children.forEach((child) => {
              if (!newSelectedValues.includes(child.value)) {
                newSelectedValues.push(child.value);
              }
            });
          }
        });
      } else {
        // Проверяем, является ли добавленная опция родительской
        const parentOption = safetyStandardsNestedOptions.find((opt) => opt.value === addedValue);
        if (parentOption && parentOption.children) {
          // Если выбран родитель, добавляем все его дочерние опции
          parentOption.children.forEach((child) => {
            if (!newSelectedValues.includes(child.value)) {
              newSelectedValues.push(child.value);
            }
          });
        }
      }
    });

    // Обрабатываем удаленные опции
    removedValues.forEach((removedValue: string) => {
      // Если снят выбор с "Все", снимаем выбор со всех опций
      if (removedValue === 'all') {
        safetyStandardsNestedOptions.forEach((option) => {
          if (option.value !== 'all') {
            const index = newSelectedValues.indexOf(option.value);
            if (index > -1) {
              newSelectedValues.splice(index, 1);
            }
            if (option.children) {
              option.children.forEach((child) => {
                const childIndex = newSelectedValues.indexOf(child.value);
                if (childIndex > -1) {
                  newSelectedValues.splice(childIndex, 1);
                }
              });
            }
          }
        });
      } else {
        // Проверяем, является ли удаленная опция родительской
        const parentOption = safetyStandardsNestedOptions.find((opt) => opt.value === removedValue);
        if (parentOption && parentOption.children) {
          // Если снят выбор с родителя, снимаем выбор со всех дочерних опций
          parentOption.children.forEach((child) => {
            const childIndex = newSelectedValues.indexOf(child.value);
            if (childIndex > -1) {
              newSelectedValues.splice(childIndex, 1);
            }
          });
        } else {
          // Если снят выбор с дочерней опции, проверяем родителя
          const parentOption = safetyStandardsNestedOptions.find((opt) => 
            opt.children && opt.children.some((child) => child.value === removedValue)
          );
          if (parentOption) {
            // Проверяем, все ли дочерние опции выбраны
            const allChildrenSelected = parentOption.children?.every((child) => 
              newSelectedValues.includes(child.value)
            );
            // Если не все дочерние опции выбраны, снимаем выбор с родителя
            if (!allChildrenSelected) {
              const parentIndex = newSelectedValues.indexOf(parentOption.value);
              if (parentIndex > -1) {
                newSelectedValues.splice(parentIndex, 1);
              }
            }
          }
        }
      }
    });

    // После обработки добавленных/удаленных опций, проверяем все родительские опции
    safetyStandardsNestedOptions.forEach((parent) => {
      if (parent.children && parent.children.length > 0) {
        const allChildrenSelected = parent.children.every((child) => 
          newSelectedValues.includes(child.value)
        );
        if (allChildrenSelected) {
          // Если все дочерние опции выбраны, добавляем родителя
          if (!newSelectedValues.includes(parent.value)) {
            newSelectedValues.push(parent.value);
          }
        } else {
          // Если не все дочерние опции выбраны, убираем родителя
          const parentIndex = newSelectedValues.indexOf(parent.value);
          if (parentIndex > -1) {
            newSelectedValues.splice(parentIndex, 1);
          }
        }
      }
    });

    // Проверяем, выбраны ли все опции (кроме "Все")
    const allOptionsSelected = safetyStandardsNestedOptions
      .filter((opt) => opt.value !== 'all')
      .every((option) => {
        if (option.children) {
          return option.children.every((child) => newSelectedValues.includes(child.value));
        }
        return newSelectedValues.includes(option.value);
      });

    if (allOptionsSelected && !newSelectedValues.includes('all')) {
      newSelectedValues.push('all');
    } else if (!allOptionsSelected && newSelectedValues.includes('all')) {
      const allIndex = newSelectedValues.indexOf('all');
      if (allIndex > -1) {
        newSelectedValues.splice(allIndex, 1);
      }
    }

    setSelectedSafetyStandards(newSelectedValues);
    form.setFieldsValue({ safetyStandardsIds: newSelectedValues });
  };
  
  // Функция для кастомного рендера опций с числом
  const optionRender = (option: any) => {
    const data = option.data || option;
    return (
      <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%'}}>
        <span>{data.label}</span>
        {data.count !== undefined && (
          <span className="option-count" style={{marginLeft: 'auto', color: '#8c8c8b', fontSize: '14px', fontWeight: 500}}>{data.count}</span>
        )}
      </div>
    );
  };

  useEffect(() => {
    if (isModalVisible) {
      const currentYear = new Date().getFullYear();
      const minYear = currentYear - 100;
      
      // Восстанавливаем сохраненные значения или используем значения по умолчанию
      const savedProductionYearRange = initialValues?.productionYearRange || [minYear, currentYear];
      const savedOverhaulYearRange = initialValues?.overhaulYearRange || [minYear, currentYear];
      const savedFlightDurationRange = initialValues?.flightDurationRange || [1, 12];
      const savedSafetyStandards = initialValues?.safetyStandardsIds || [];
      
      form.setFieldsValue({
        companionsAllowed: initialValues?.companionsAllowed || false,
        productionYearRange: savedProductionYearRange,
        overhaulYearRange: savedOverhaulYearRange,
        flightDurationRange: savedFlightDurationRange,
        planeModelIds: initialValues?.planeModelIds || [],
        landingsCount: initialValues?.landingsCount || [],
        refuelsCount: initialValues?.refuelsCount || [],
        planeUpdateDate: initialValues?.planeUpdateDate || undefined,
        amenitiesIds: initialValues?.amenitiesIds || [],
        operatorIds: initialValues?.operatorIds || [],
        safetyStandardsIds: savedSafetyStandards,
        comment: initialValues?.comment || '',
      });
      
      setProductionYearRange(savedProductionYearRange);
      setOverhaulYearRange(savedOverhaulYearRange);
      setFlightDurationRange(savedFlightDurationRange);
      setSelectedSafetyStandards(savedSafetyStandards);
    }
  }, [isModalVisible, form, initialValues]);

  const handleClose = () => {
    setIsModalVisible(false);
  };

  const handleSave = () => {
    form.validateFields().then(() => {
      // Получаем все значения формы, включая ползунки
      const allValues = form.getFieldsValue();
      // Убеждаемся, что значения ползунков включены (они могут не пройти валидацию)
      const values = {
        ...allValues,
        productionYearRange: allValues.productionYearRange || productionYearRange,
        overhaulYearRange: allValues.overhaulYearRange || overhaulYearRange,
        flightDurationRange: allValues.flightDurationRange || flightDurationRange,
        safetyStandardsIds: allValues.safetyStandardsIds || selectedSafetyStandards,
      };
      if (onSave) {
        onSave(values);
      }
      handleClose();
    });
  };

  return (
    <>
      <AdvancedSearchModalDropdownStyles parentOptionIndexes={parentOptionIndexes} enableIndeterminate={true} />
      <StyledModal
      open={isModalVisible}
      onCancel={handleClose}
      footer={null}
      width={592}
      centered
      closable={false}
    >
      <ModalHeader>
        <ModalTitle>{intl.formatMessage({id: 'newTripRequestPage.advancedSearch', defaultMessage: 'Расширенный поиск'})}</ModalTitle>
        <CloseButton onClick={handleClose}>
          <CloseOutlined />
        </CloseButton>
      </ModalHeader>

      <Form
        form={form}
        layout="vertical"
        onValuesChange={(changedValues) => {
          if (changedValues.productionYearRange) {
            setProductionYearRange(changedValues.productionYearRange);
          }
          if (changedValues.overhaulYearRange) {
            setOverhaulYearRange(changedValues.overhaulYearRange);
          }
          if (changedValues.flightDurationRange) {
            setFlightDurationRange(changedValues.flightDurationRange);
          }
        }}
      >
        <ModalContent>
          <CustomFieldInput
            label={intl.formatMessage({id: 'app.form.labels.planeModel', defaultMessage: 'Модель самолета'})}
            name="planeModelIds"
          >
            <DebounceSelect
              fetchOptions={fetchPlaneModels}
              placeholder={intl.formatMessage({id: 'app.form.placeholders.selectPlaneModels', defaultMessage: 'Выберите модели самолета'})}
              mode="multiple"
              showSearch={true}
              popupClassName="ant-select-dropdown-multiple"
              getPopupContainer={trigger => trigger.parentNode as HTMLElement}
            />
          </CustomFieldInput>

          <CustomFieldInput
            label={intl.formatMessage({id: 'app.form.labels.productionYear', defaultMessage: 'Год выпуска самолёта'})}
            name="productionYearRange"
            style={{width: '100%'}}
          >
            <div style={{width: '100%', minWidth: 0}}>
              <SliderWrapper>
                <Form.Item name="productionYearRange" noStyle style={{width: '100%', margin: 0}}>
                  <StyledSlider
                    range
                    min={minYear}
                    max={currentYear}
                    value={productionYearRange}
                    onChange={(value) => {
                      setProductionYearRange(value as [number, number]);
                      form.setFieldsValue({productionYearRange: value});
                    }}
                  />
                </Form.Item>
                <SliderLabels>
                  <span>{productionYearRange[0]}</span>
                  <span>{productionYearRange[1]}</span>
                </SliderLabels>
              </SliderWrapper>
            </div>
          </CustomFieldInput>

          <CustomFieldInput
            label={intl.formatMessage({id: 'app.form.labels.overhaulYear', defaultMessage: 'Год последнего капитального ремонта самолета'})}
            name="overhaulYearRange"
            style={{width: '100%'}}
          >
            <div style={{width: '100%', minWidth: 0}}>
              <SliderWrapper>
                <Form.Item name="overhaulYearRange" noStyle style={{width: '100%', margin: 0}}>
                  <StyledSlider
                    range
                    min={minYear}
                    max={currentYear}
                    value={overhaulYearRange}
                    onChange={(value) => {
                      setOverhaulYearRange(value as [number, number]);
                      form.setFieldsValue({overhaulYearRange: value});
                    }}
                  />
                </Form.Item>
                <SliderLabels>
                  <span>{overhaulYearRange[0]}</span>
                  <span>{overhaulYearRange[1]}</span>
                </SliderLabels>
              </SliderWrapper>
            </div>
          </CustomFieldInput>

          <CustomFieldInput
            label={intl.formatMessage({id: 'app.form.labels.flightDuration', defaultMessage: 'Длительность подлета'})}
            name="flightDurationRange"
            style={{width: '100%'}}
          >
            <div style={{width: '100%', minWidth: 0}}>
              <SliderWrapper>
                <Form.Item name="flightDurationRange" noStyle style={{width: '100%', margin: 0}}>
                  <StyledSlider
                    range
                    min={1}
                    max={12}
                    value={flightDurationRange}
                    onChange={(value) => {
                      setFlightDurationRange(value as [number, number]);
                      form.setFieldsValue({flightDurationRange: value});
                    }}
                  />
                </Form.Item>
                <SliderLabels>
                  <span>{intl.formatMessage({id: 'app.form.slider.fromHours', defaultMessage: 'from {hours} hour'}, {hours: flightDurationRange[0]})}</span>
                  <span>{intl.formatMessage({id: 'app.form.slider.toHours', defaultMessage: 'to {hours} hours'}, {hours: flightDurationRange[1]})}</span>
                </SliderLabels>
              </SliderWrapper>
            </div>
          </CustomFieldInput>

          <Row gutter={8}>
            <Col span={12}>
              <CustomFieldInput
                label={intl.formatMessage({id: 'app.form.labels.landingsCount', defaultMessage: 'Количество посадок'})}
                name="landingsCount"
              >
               <DebounceSelect
                 fetchOptions={async () => landingsOptions.map(opt => ({ label: opt.label, value: opt.value }))}
                 placeholder={intl.formatMessage({id: 'app.form.placeholders.selectCount', defaultMessage: 'Выберите количество'})}
                 mode="multiple"
                 showSearch={true}
                 popupClassName="ant-select-dropdown-multiple"
                 getPopupContainer={trigger => trigger.parentNode as HTMLElement}
               />
              </CustomFieldInput>
            </Col>
            <Col span={12}>
              <CustomFieldInput
                label={intl.formatMessage({id: 'app.form.labels.refuelsCount', defaultMessage: 'Количество дозаправок в пути'})}
                name="refuelsCount"
              >
               <DebounceSelect
                 fetchOptions={async () => refuelsOptions.map(opt => ({ label: opt.label, value: opt.value }))}
                 placeholder={intl.formatMessage({id: 'app.form.placeholders.selectCount', defaultMessage: 'Выберите количество'})}
                 mode="multiple"
                 showSearch={true}
                 popupClassName="ant-select-dropdown-multiple"
                 getPopupContainer={trigger => trigger.parentNode as HTMLElement}
               />
              </CustomFieldInput>
            </Col>
          </Row>

          <CustomFieldInput
            label={intl.formatMessage({id: 'app.form.labels.planeUpdateDate', defaultMessage: 'Дата обновления самолета'})}
            name="planeUpdateDate"
          >
            <CustomDatePicker
              placeholder={intl.formatMessage({id: 'app.form.placeholders.selectDate', defaultMessage: 'Выберите дату'})}
              style={{width: '100%'}}
            />
          </CustomFieldInput>

          <CustomFieldInput
            label={intl.formatMessage({id: 'app.form.labels.onboardAmenities', defaultMessage: 'Удобства на борту'})}
            name="amenitiesIds"
          >
              <DebounceSelect
                fetchOptions={async () => amenitiesOptions.map(opt => ({ label: opt.label, value: opt.value }))}
                placeholder={intl.formatMessage({id: 'app.form.placeholders.selectAmenities', defaultMessage: 'Выберите удобства'})}
                mode="multiple"
                showSearch={true}
                popupClassName="ant-select-dropdown-multiple"
                getPopupContainer={trigger => trigger.parentNode as HTMLElement}
              />
          </CustomFieldInput>

          <CustomFieldInput
            label={intl.formatMessage({id: 'app.form.labels.operator', defaultMessage: 'Оператор'})}
            name="operatorIds"
          >
            <DebounceSelect
              fetchOptions={fetchOperators}
              placeholder={intl.formatMessage({id: 'app.form.placeholders.selectOperators', defaultMessage: 'Выберите операторов'})}
              mode="multiple"
              showSearch={true}
              popupClassName="ant-select-dropdown-multiple"
              getPopupContainer={trigger => trigger.parentNode as HTMLElement}
            />
          </CustomFieldInput>

          <CustomFieldInput
            label={intl.formatMessage({id: 'app.form.labels.safetyStandards', defaultMessage: 'Стандарты безопасности'})}
            name="safetyStandardsIds"
          >
              <DebounceSelect
                fetchOptions={async () => safetyStandardsOptions}
                placeholder={intl.formatMessage({id: 'app.form.placeholders.selectSafetyStandards', defaultMessage: 'Выберите стандарты безопасности'})}
                mode="multiple"
                showSearch={true}
                value={selectedSafetyStandards as any}
                onChange={handleSafetyStandardsChange as any}
                enableIndeterminate={true}
                placement="bottomLeft"
                popupClassName="ant-select-dropdown-multiple"
                getPopupContainer={trigger => trigger.parentNode as HTMLElement}
              />
          </CustomFieldInput>

          <CustomFieldInput
            label={intl.formatMessage(CommonFormMessages.commentLabel)}
            name="comment"
          >
            <TextArea
              allowClear={true}
              autoSize={{minRows: 3}}
              maxLength={2000}
              placeholder={intl.formatMessage(CommonFormMessages.commentPlaceholder)}
            />
          </CustomFieldInput>

          <CustomFieldInput
            name="companionsAllowed"
            valuePropName="checked"
          >
            <CustomSwitch label={intl.formatMessage(CommonDetailsMessages.companionsAllowed)}/>
          </CustomFieldInput>
        </ModalContent>

        <ModalFooter>
          <CustomButton
            type="primary"
            onClick={handleSave}
            block
            style={{height: '47px', fontSize: '16px'}}
          >
            {intl.formatMessage(CommonButtonMessages.save)}
          </CustomButton>
        </ModalFooter>
      </Form>
    </StyledModal>
    </>
  );
};

